import { createPermission } from '@backstage/plugin-permission-common';
import { PromotableEnvironment } from './releaseModel';

/**
 * Permissions for cutting and reverting Catalyst releases.
 *
 * Declared here, in the common library, so the frontend (`usePermission`) and
 * the scaffolder action (`permissions.authorize`) import the *same* object
 * rather than two copies kept in sync by hand. The cost feature in this repo
 * does keep two copies, and says so in a comment — that is the pattern this
 * package exists to avoid.
 *
 * Enforcement lives on the scaffolder action, because that is what pushes the
 * tag. The UI disables what the operator cannot do as a courtesy; it is not
 * the control.
 *
 * Reading deployments is deliberately ungated: it is the same information the
 * gitops repository already shows.
 */
export const releasePromotePermission = createPermission({
  name: 'release.promote',
  attributes: { action: 'create' },
});

export const releaseRollbackPermission = createPermission({
  name: 'release.rollback',
  attributes: { action: 'create' },
});

export const releasePromoteProdPermission = createPermission({
  name: 'release.promote.prod',
  attributes: { action: 'create' },
});

export const releaseRollbackProdPermission = createPermission({
  name: 'release.rollback.prod',
  attributes: { action: 'create' },
});

/**
 * Changing what an overlay holds: configMap values, replicas, resources.
 *
 * Split by environment the same way releases are, and for the same reason —
 * `authorize` is only ever handed a permission, never the arguments of the
 * call, so "prod needs more" has to be a second permission object or it is a
 * comment. `dev` is a legitimate target here, unlike for releases: the image
 * updater rewrites only the image tag, and configuration in dev has always
 * been a person editing the file.
 */
export const releaseConfigurePermission = createPermission({
  name: 'release.configure',
  attributes: { action: 'update' },
});

export const releaseConfigureProdPermission = createPermission({
  name: 'release.configure.prod',
  attributes: { action: 'update' },
});

export function configurePermissionFor(environment: string) {
  return environment === 'prod'
    ? releaseConfigureProdPermission
    : releaseConfigurePermission;
}

export type ReleaseAction = 'promote' | 'rollback';

export function releasePermissionFor(
  action: ReleaseAction,
  environment: PromotableEnvironment,
) {
  if (environment === 'prod') {
    return action === 'rollback'
      ? releaseRollbackProdPermission
      : releasePromoteProdPermission;
  }
  return action === 'rollback'
    ? releaseRollbackPermission
    : releasePromotePermission;
}

/** Every permission this plugin owns, for registration with the permission
 * backend. */
export const releasePermissions = [
  releasePromoteProdPermission,
  releaseRollbackProdPermission,
  releasePromotePermission,
  releaseRollbackPermission,
  releaseConfigurePermission,
  releaseConfigureProdPermission,
];
